import { MenuData, MenuCategory, MenuItem, ChoiceGroup, ChoiceOption } from '@/types';

export type MenuHealthIssueKey =
  | 'empty_menu'
  | 'empty_category'
  | 'missing_image'
  | 'missing_desc_en'
  | 'missing_desc_ar'
  | 'missing_name_ar'
  | 'invalid_price'
  | 'cg_required_optional_conflict'
  | 'cg_optional_min_not_zero'
  | 'cg_required_min_invalid'
  | 'cg_max_less_than_min'
  | 'cg_limits_exceed_options'
  | 'cg_required_no_options'
  | 'cg_option_invalid_price'
  | 'cg_option_duplicate_name';

export interface MenuHealthIssue {
  key: MenuHealthIssueKey;
  label: string;
  count: number;
  itemIds?: string[];
  categoryIds?: string[];
  severity?: 'info' | 'warn' | 'error';
}

export interface MenuHealthAnalysis {
  score: number;
  issues: MenuHealthIssue[];
  totals: {
    categories: number;
    items: number;
  };
}

const pushIssue = (
  issues: MenuHealthIssue[],
  key: MenuHealthIssueKey,
  label: string,
  count: number,
  itemIds?: string[],
  categoryIds?: string[],
  severity: 'info' | 'warn' | 'error' = 'warn'
) => {
  issues.push({
    key,
    label,
    count,
    itemIds,
    categoryIds,
    severity,
  });
};

const isArabicSupported = (menu: MenuData) => {
  if (menu.title?.ar) return true;
  return menu.categories.some(
    (cat) =>
      Boolean(cat.name?.ar) ||
      cat.items.some((item) => Boolean(item.name?.ar) || Boolean(item.description?.ar))
  );
};

const normalizeText = (value?: string | null) => (value || '').trim();

const collectDupNames = (options: ChoiceOption[]) => {
  const seen = new Map<string, string>();
  const dupIds: string[] = [];
  options.forEach((opt) => {
    const key = normalizeText(opt.name?.en || '').toLowerCase();
    if (!key) return;
    if (seen.has(key)) {
      dupIds.push(opt.id);
    } else {
      seen.set(key, opt.id);
    }
  });
  return dupIds;
};

export const analyzeMenuHealth = (menu: MenuData): MenuHealthAnalysis => {
  const issues: MenuHealthIssue[] = [];
  const arabicEnabled = isArabicSupported(menu);

  const categoryIdsEmpty: string[] = [];
  const missingImages: string[] = [];
  const missingDescEn: string[] = [];
  const missingNameAr: string[] = [];
  const missingDescAr: string[] = [];
  const invalidPrice: string[] = [];

  if (!menu.categories.length) {
    pushIssue(issues, 'empty_menu', 'Empty menu (no categories)', 1, [], [], 'error');
  }

  menu.categories.forEach((cat) => {
    if (!cat.items.length) {
      categoryIdsEmpty.push(cat.id);
    }
    cat.items.forEach((item) => {
      if (!item.image) missingImages.push(item.id);
      if (!normalizeText(item.description?.en)) missingDescEn.push(item.id);
      if (arabicEnabled && !normalizeText(item.name?.ar)) missingNameAr.push(item.id);
      if (arabicEnabled && !normalizeText(item.description?.ar)) missingDescAr.push(item.id);
      if (!Number.isFinite(item.price) || item.price <= 0) invalidPrice.push(item.id);
    });
  });

  pushIssue(issues, 'empty_category', 'Empty categories', categoryIdsEmpty.length, [], categoryIdsEmpty, 'error');
  pushIssue(issues, 'missing_image', 'Items missing image', missingImages.length, missingImages);
  pushIssue(issues, 'missing_desc_en', 'Items missing description (EN)', missingDescEn.length, missingDescEn);
  if (arabicEnabled) {
    pushIssue(issues, 'missing_name_ar', 'Items missing name (AR)', missingNameAr.length, missingNameAr);
    pushIssue(issues, 'missing_desc_ar', 'Items missing description (AR)', missingDescAr.length, missingDescAr);
  }
  pushIssue(issues, 'invalid_price', 'Items with invalid price', invalidPrice.length, invalidPrice, [], 'error');

  // Choice group checks
  const optionalMinInvalid: string[] = [];
  const requiredMinInvalid: string[] = [];
  const requiredOptionalConflict: string[] = [];
  const maxLessThanMin: string[] = [];
  const limitsExceedOptions: string[] = [];
  const requiredNoOptions: string[] = [];
  const optionInvalidPrice: string[] = [];
  const optionDuplicateName: string[] = [];

  const addGroupIssue = (target: string[], item: MenuItem) => {
    target.push(item.id);
  };

  const addOptionIssue = (target: string[], item: MenuItem, optionIds: string[]) => {
    if (optionIds.length) target.push(item.id);
  };

  const categories: MenuCategory[] = menu.categories || [];
  categories.forEach((cat) => {
    cat.items.forEach((item) => {
      (item.choiceGroups || []).forEach((group) => {
        const optCount = group.options?.length || 0;

        if (group.required && group.optional) addGroupIssue(requiredOptionalConflict, item);
        if (!group.required && group.min > 0) addGroupIssue(optionalMinInvalid, item);
        if (group.required && group.min < 1) addGroupIssue(requiredMinInvalid, item);
        if (group.max > 0 && group.max < group.min) addGroupIssue(maxLessThanMin, item);
        if ((group.min > optCount) || (group.max > optCount && group.max > 0)) addGroupIssue(limitsExceedOptions, item);
        if (group.required && optCount === 0) addGroupIssue(requiredNoOptions, item);

        const badPrices = (group.options || []).filter(
          (opt) => !Number.isFinite(opt.priceModifier || 0) || (opt.priceModifier ?? 0) < 0
        );
        addOptionIssue(optionInvalidPrice, item, badPrices.map((b) => b.id));

        const dupIds = collectDupNames(group.options || []);
        addOptionIssue(optionDuplicateName, item, dupIds);
      });
    });
  });

  pushIssue(issues, 'cg_required_optional_conflict', 'Choice group: required/optional conflict', requiredOptionalConflict.length, requiredOptionalConflict);
  pushIssue(issues, 'cg_optional_min_not_zero', 'Choice group: optional min must be 0', optionalMinInvalid.length, optionalMinInvalid);
  pushIssue(issues, 'cg_required_min_invalid', 'Choice group: required min must be >= 1', requiredMinInvalid.length, requiredMinInvalid);
  pushIssue(issues, 'cg_max_less_than_min', 'Choice group: max less than min', maxLessThanMin.length, maxLessThanMin);
  pushIssue(issues, 'cg_limits_exceed_options', 'Choice group: min/max exceed options', limitsExceedOptions.length, limitsExceedOptions);
  pushIssue(issues, 'cg_required_no_options', 'Choice group: required group has no options', requiredNoOptions.length, requiredNoOptions);
  pushIssue(issues, 'cg_option_invalid_price', 'Choice option: invalid price modifier', optionInvalidPrice.length, optionInvalidPrice);
  pushIssue(issues, 'cg_option_duplicate_name', 'Choice option: duplicate names', optionDuplicateName.length, optionDuplicateName);

  const totalHits = issues.reduce((sum, issue) => sum + issue.count, 0);
  const score = Math.max(0, Math.round(100 - Math.min(100, totalHits * 4)));

  return {
    score,
    issues,
    totals: {
      categories: menu.categories.length,
      items: menu.categories.reduce((acc, c) => acc + c.items.length, 0),
    },
  };
};
